// @flow
// -----------------------------------------------------------------
// <copyright                                                       
//   file="Izenda.Request.js"                                       
//   company="Izenda">                                              
//  Copyright (c) 2015 Izenda, Inc.                                 
//  ALL RIGHTS RESERVED                                             
//                                                                  
//  The entire contents of this file is protected by U.S. and       
//  International Copyright Laws. Unauthorized reproduction,        
//  reverse-engineering, and distribution of all or any portion of  
//  the code contained in this file is strictly prohibited and may  
//  result in severe civil and criminal penalties and will be       
//  prosecuted to the maximum extent possible under the law.        
//                                                                  
//  RESTRICTIONS                                                    
//                                                                  
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES           
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE                          
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS              
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET    
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.                 
//                                                                  
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED      
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE        
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE       
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT  
//  AND PERMISSION FROM IZENDA INC.                                 
//                                                                  
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  
//  ADDITIONAL RESTRICTIONS.                                        
// </copyright>                                                     
// -----------------------------------------------------------------

import tracker from 'share/tracking/Izenda.Tracker';
import { observable, action, computed } from "mobx";
import { map, isEmpty, last, filter } from 'lodash/fp';

export const IZENDA_REQUEST_BODY_LANGUAGES = {
  TEXT: 'text',
  JSON: 'json',
  XML: 'xml'
};

@tracker
export default class IzendaRequest {
    @observable headers: IzendaRequestHeader[] = [];
    @observable body: string = '';
    @observable bodyLanguage: string = IZENDA_REQUEST_BODY_LANGUAGES.TEXT;

    constructor(headers: IzendaRequestHeader[], body: string, bodyLanguage: string) {
      this.init(headers, body, bodyLanguage);
    }

    @action
    init(headers: IzendaRequestHeader[], body: string, bodyLanguage: string) {
      this.body = body;
      this.bodyLanguage = bodyLanguage;
      this.headers = headers;
      this.addEmptyLastHeader();
    }

    @action
    setBody(val: string) {
      if (this.body === val)
        return;
      this.body = val;
      this.markDirty();
    }

    @action
    setBodyLanguage(val: string) {
      if (this.bodyLanguage === val)
        return;
      this.bodyLanguage = val;
      this.markDirty();
    }

    @action
    reset() {
        this.headers = [];
        this.body = '';
        this.resetDirty();
    }

    @action
    pushHeader(header: IzendaRequestHeader) {
      this.headers.splice(this.headers.length - 1, 0, header);
    }

    @action
    removeHeader(header: IzendaRequestHeader) {
      this.headers.splice(this.headers.indexOf(header), 1);
      this.addEmptyLastHeader();
    }

    @action
    addEmptyLastHeader() {
      if (!this.hasEmptyLastHeader()) {
        this.headers.push(new IzendaRequestHeader(true, '', ''));
      }
    }

    @computed
    get headersCount() {
      return filter(x => !!x.name, this.headers).length;
    }

    // Got from https://developer.mozilla.org/ru/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
    static b64EncodeUnicode(str) {
      // first we use encodeURIComponent to get percent-encoded UTF-8,
      // then we convert the percent encodings into raw bytes which
      // can be fed into btoa.
      return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
        function toSolidBytes(match, p1) {
          return String.fromCharCode('0x' + p1);
        }));
    }

    get bodyBase64() {
      return IzendaRequest.b64EncodeUnicode(this.body);
    }

    hasEmptyLastHeader() {
      if (isEmpty(this.headers))
        return false;
      const lastHeader = last(this.headers);
      return !lastHeader.name;
    }

    clone(): IzendaRequest {
      const result = new IzendaRequest([], this.body, this.bodyLanguage);
      result.headers = map(x => x.clone(), this.headers);
      return result;
    }
}

@tracker
export class IzendaRequestHeader {
    @observable enabled: boolean = true;
    @observable name: string = null;
    @observable value: string = null;

    constructor(enabled: boolean, name: string, value: string) {
        this.init(enabled, name, value);
    }

    @action
    init(enabled: boolean, name: string, value: string) {
      this.enabled = enabled;
      this.name = name;
      this.value = value;
    }

    @action
    setEnabled(val: boolean) {
      if (this.enabled === val)
        return;
      this.enabled = !!val;
      this.markDirty();
    }

    @action
    setName(newName: string) {
      if (this.name === newName)
        return;
      this.name = newName;
      this.markDirty();
    }

    @action
    setValue(newValue: string) {
      if (this.value === newValue)
        return;
      this.value = newValue;
      this.markDirty();
    }

    clone(): IzendaRequestHeader {
      return new IzendaRequestHeader(this.enabled, this.name, this.value);
    }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.Request.js